import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ChevronUp, ChevronDown, X, Filter } from 'lucide-react';

const TableContainer = styled.div`
  width: 100%;
  overflow: hidden;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const TableHeader = styled.thead`
  background: linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%);

  th {
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    color: white;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    font-size: 13px;
    text-transform: capitalize;

    &:not(:last-child) {
      border-right: 1px solid rgba(255, 255, 255, 0.1);
    }
  }
`;

const FilterRow = styled.tr`
  background: #f8fafc;

  th {
    padding: 8px 12px;
    background: #f8fafc;
    border-bottom: 2px solid #e2e8f0;

    &:not(:last-child) {
      border-right: 1px solid #e2e8f0;
    }
  }
`;

const FilterCell = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
`;

const FilterInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  font-size: 12px;
  color: #2d3748;
  background: white;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #1A8B8B;
    box-shadow: 0 0 0 3px rgba(26, 139, 139, 0.1);
  }

  &::placeholder {
    color: #a0aec0;
  }
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  font-size: 12px;
  color: #2d3748;
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #1A8B8B;
    box-shadow: 0 0 0 3px rgba(26, 139, 139, 0.1);
  }
`;

const ClearFilterButton = styled.button`
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  background: #e2e8f0;
  border: none;
  border-radius: 3px;
  padding: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  transition: all 0.2s;

  &:hover {
    opacity: 1;
    background: #cbd5e0;
  }

  svg {
    width: 12px;
    height: 12px;
    color: #4a5568;
  }
`;

const FilterToggle = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const TableBody = styled.tbody`
  tr {
    transition: background-color 0.2s ease;

    &:nth-child(even) {
      background-color: #f8fafc;
    }

    &:hover {
      background-color: #edf2f7;
    }

    &:last-child td {
      border-bottom: none;
    }
  }

  td {
    padding: 12px 16px;
    border-bottom: 1px solid #e2e8f0;
    color: #2d3748;
    vertical-align: top;
    line-height: 1.4;
    word-wrap: break-word;
    max-width: 200px;

    &:not(:last-child) {
      border-right: 1px solid #f1f5f9;
    }
  }
`;

const TableMeta = styled.div`
  padding: 8px 16px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  font-size: 12px;
  color: #718096;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  span {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const FilterStatus = styled.span`
  background: #1A8B8B;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
`;

const ClearAllFiltersButton = styled.button`
  background: #e53e3e;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;

  &:hover {
    background: #c53030;
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const NoDataMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #718096;
  font-style: italic;
  background: #f8fafc;
`;

const TableExpandControl = styled.div`
  padding: 12px 16px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: center;

  button {
    background: linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%);
    color: white;
    border: none;
    border-radius: 6px;
    padding: 8px 16px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(26, 139, 139, 0.3);
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

// Helper function to format cell values
const formatCellValue = (value) => {
  // Handle null/undefined values
  if (value === null || value === undefined) {
    return <span style={{color: '#9ca3af', fontStyle: 'italic'}}>null</span>;
  }

  // Handle empty strings
  if (value === '') {
    return <span style={{color: '#9ca3af', fontStyle: 'italic'}}>empty</span>;
  }

  // Handle numbers with decimals
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  }

  // Handle long strings (truncate if very long)
  if (typeof value === 'string' && value.length > 100) {
    return (
      <span title={value}>
        {value.substring(0, 97)}...
      </span>
    );
  }

  // Default: convert to string
  return value.toString();
};

// Detect if column has limited unique values (good for dropdown)
const getUniqueValues = (results, colIndex) => {
  const values = results.map(row => row[colIndex]);
  const unique = [...new Set(values.filter(v => v !== null && v !== undefined))];
  return unique.sort();
};

const isColumnCategorical = (results, colIndex, threshold = 20) => {
  const uniqueValues = getUniqueValues(results, colIndex);
  return uniqueValues.length <= threshold && uniqueValues.length > 0;
};

const DataTable = ({ columnNames, results }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});

  const INITIAL_ROWS = 5; // Show first 5 rows by default

  // Analyze columns for filter types
  const columnMetadata = useMemo(() => {
    if (!results || results.length === 0) return [];

    return columnNames.map((name, index) => ({
      name,
      index,
      isCategorical: isColumnCategorical(results, index),
      uniqueValues: isColumnCategorical(results, index)
        ? getUniqueValues(results, index)
        : []
    }));
  }, [columnNames, results]);

  // Apply filters to results
  const filteredResults = useMemo(() => {
    if (!results || Object.keys(columnFilters).length === 0) {
      return results;
    }

    return results.filter(row => {
      return Object.entries(columnFilters).every(([colIndex, filterValue]) => {
        if (!filterValue || filterValue === '') return true;

        const cellValue = row[parseInt(colIndex)];
        if (cellValue === null || cellValue === undefined) return false;

        const cellStr = cellValue.toString().toLowerCase();
        const filterStr = filterValue.toLowerCase();

        return cellStr.includes(filterStr);
      });
    });
  }, [results, columnFilters]);

  const handleFilterChange = (colIndex, value) => {
    setColumnFilters(prev => {
      if (!value || value === '') {
        const newFilters = { ...prev };
        delete newFilters[colIndex];
        return newFilters;
      }
      return { ...prev, [colIndex]: value };
    });
  };

  const clearFilter = (colIndex) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[colIndex];
      return newFilters;
    });
  };

  const clearAllFilters = () => {
    setColumnFilters({});
  };

  const activeFilterCount = Object.keys(columnFilters).length;

  if (!columnNames || !results || results.length === 0) {
    return (
      <TableContainer>
        <NoDataMessage>
          📊 No data available to display
        </NoDataMessage>
      </TableContainer>
    );
  }

  const shouldCollapse = filteredResults.length > INITIAL_ROWS;
  const displayedResults = shouldCollapse && !isExpanded
    ? filteredResults.slice(0, INITIAL_ROWS)
    : filteredResults;
  const hiddenRowsCount = filteredResults.length - INITIAL_ROWS;

  return (
    <TableContainer>
      <StyledTable>
        <TableHeader>
          <tr>
            {columnNames.map((column, index) => (
              <th key={index}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <span>{column}</span>
                  {index === columnNames.length - 1 && (
                    <FilterToggle onClick={() => setShowFilters(!showFilters)}>
                      <Filter size={12} />
                      {showFilters ? 'Hide' : 'Filter'}
                    </FilterToggle>
                  )}
                </div>
              </th>
            ))}
          </tr>

          {/* Filter Row */}
          {showFilters && (
            <FilterRow>
              {columnMetadata.map((col) => (
                <th key={col.index}>
                  <FilterCell>
                    {col.isCategorical ? (
                      // Dropdown for categorical columns
                      <FilterSelect
                        value={columnFilters[col.index] || ''}
                        onChange={(e) => handleFilterChange(col.index, e.target.value)}
                      >
                        <option value="">All</option>
                        {col.uniqueValues.map((value, i) => (
                          <option key={i} value={value}>
                            {value}
                          </option>
                        ))}
                      </FilterSelect>
                    ) : (
                      // Text input for other columns
                      <FilterInput
                        type="text"
                        placeholder="Filter..."
                        value={columnFilters[col.index] || ''}
                        onChange={(e) => handleFilterChange(col.index, e.target.value)}
                      />
                    )}

                    {columnFilters[col.index] && (
                      <ClearFilterButton onClick={() => clearFilter(col.index)}>
                        <X />
                      </ClearFilterButton>
                    )}
                  </FilterCell>
                </th>
              ))}
            </FilterRow>
          )}
        </TableHeader>
        <TableBody>
          {displayedResults.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columnNames.map((_, colIndex) => (
                <td key={colIndex}>
                  {formatCellValue(row[colIndex])}
                </td>
              ))}
            </tr>
          ))}
        </TableBody>
      </StyledTable>

      {/* No results message */}
      {filteredResults.length === 0 && (
        <NoDataMessage>
          🔍 No results match your filters
        </NoDataMessage>
      )}

      {/* Collapse/Expand Controls */}
      {shouldCollapse && filteredResults.length > 0 && (
        <TableExpandControl>
          <motion.button
            onClick={() => setIsExpanded(!isExpanded)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isExpanded ? (
              <>
                <ChevronUp size={16} />
                Show Less ({hiddenRowsCount} rows hidden)
              </>
            ) : (
              <>
                <ChevronDown size={16} />
                Show {hiddenRowsCount} More Rows
              </>
            )}
          </motion.button>
        </TableExpandControl>
      )}

      <TableMeta>
        <span>📊 {filteredResults.length} of {results.length} row{results.length !== 1 ? 's' : ''}</span>
        <span>📋 {columnNames.length} column{columnNames.length !== 1 ? 's' : ''}</span>
        {activeFilterCount > 0 && (
          <>
            <FilterStatus>
              {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
            </FilterStatus>
            <ClearAllFiltersButton onClick={clearAllFilters}>
              <X size={12} />
              Clear All
            </ClearAllFiltersButton>
          </>
        )}
        {shouldCollapse && filteredResults.length > 0 && (
          <span>
            {isExpanded ? 'All rows visible' : `${INITIAL_ROWS} of ${filteredResults.length} rows shown`}
          </span>
        )}
      </TableMeta>
    </TableContainer>
  );
};

export default DataTable;
